import React from 'react';

import { useTranslation } from 'react-i18next';

import { Typography, CircularProgress, Box } from '@material-ui/core';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { Img, TitlesWrapper } from './CheckYourEmail.styles';
import { PreOnboardingParams, SetupForm } from '../../PreOnboardingPage';

import SleepioThumbsUp from '@/assets/SleepioThumbsUp.svg';
import LimbotHappy from '@/assets/LimbotHappy.svg';
import { isSpark as isSparkHelper, getProductName } from '@/utils/productHelpers';
import {
  ActiveFeatureFlag,
  FeatureFlagProvider,
  useFeatureFlag,
} from '@/hooks/usePostHogFeatureFlags';

type Props = {
  isGeneric?: boolean;
};

const CheckYourEmail: React.FC<Props> = ({ isGeneric = true }) => {
  const { t } = useTranslation();
  const { product } = useParams<PreOnboardingParams>();
  const isSpark = isSparkHelper(product);

  /* kept logic commented in case we need to implement the oganization naming again
   const { data: organizationNameData } = useQuery(QUERY_ORGANIZATION_BY_SLUG, {
      variables: { organizationSlug: slug },
    }); */

  const variant = useFeatureFlag(ActiveFeatureFlag.EMPLOYER_NAME_EXPERIMENT);

  if (variant === undefined) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const flowType = isGeneric ? 'generic' : 'specific';
  const productName = getProductName(product);
  const firstTitle = t(`coverage.pre_onboarding.${flowType}.check_your_email_title`);
  const secondTitle = t(`coverage.pre_onboarding.${flowType}.check_your_email_second_title`);
  const firstDescription = t(
    `coverage.pre_onboarding.${flowType}.check_your_email_subtitle`, { productName },
  );
  const secondDescription = t(
    `coverage.pre_onboarding.${flowType}.check_your_email_second_description.${
      isSpark ? 'spark' : 'sleepio'
    }`, { productName },
  );

  return (
    <>
      <Img
        alt={isSpark ? 'Spark Waving Limbot' : 'Sleepio Thumbsup'}
        style={{ width: '206px' }}
        src={isSpark ? LimbotHappy : SleepioThumbsUp}
      />
      <TitlesWrapper data-is-generic={isGeneric || undefined}>
        <Typography variant="h2">
          {firstTitle}
        </Typography>
        <Typography style={{ marginTop: 8, marginBottom: 16 }}>
          {firstDescription}
        </Typography>
        <Typography variant="h3">
          {secondTitle}
        </Typography>
        <Typography style={{ marginTop: 8, marginBottom: 16 }}>
          {secondDescription}
        </Typography>
      </TitlesWrapper>
    </>
  );
};

export default (props: Props) => {
  const { watch } = useFormContext<SetupForm>();

  return (
    <FeatureFlagProvider email={watch('email')}>
      <CheckYourEmail {...props} />
    </FeatureFlagProvider>
  );
};
